import React, { useMemo, useCallback } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '@fs/zion-analytics'
import { useSlsStateContext } from '@fs/zion-sls'
import { ContentAdd } from '@fs/zion-icon'
import { H3, H2, Row, IconButton } from '@fs/zion-ui'
import FieldWrapper from '../../ui/FieldWrapper'
import Subsection from './Subsection'
import NoDataAddedMessage from './NoDataAddedMessage'
import IndexInfoSection from './IndexInfoSection'
import { useR2DispatchContext } from '../../../../../providers/r2Provider/R2Context'
import { useOverlayContext } from '../../../../../../shared/providers/overlayContextProvider/OverlayContext'

const indentCss = css`
  padding-inline-start: 10px;
`

const EventsSection = ({
  isRestrictedEventField,
  isRestrictedRelationshipField,
  disableEdit,
  eventFields,
  eventsConnectedToRelationships,
  relationships,
  addEventOptionsOverlay,
}) => {
  const [t] = useTranslation()
  const { openOverlay, infoSheetOverlay } = useOverlayContext()
  const zionSlsState = useSlsStateContext()
  const dispatch = useR2DispatchContext()

  const newEventClick = (ev) => {
    addEventOptionsOverlay.handleClick(ev)
    dispatch({ type: 'SET_ACTIVE_ELEMENT_ID', value: 'addingEvent' })
  }

  const connectEventToRelationship = useCallback(
    (event) => {
      const connectedRelationships = relationships.filter((relationship) =>
        event.superElements?.some((superElement) => superElement.id === relationship.id)
      )
      return { ...event, connectedRelationships }
    },
    [relationships]
  )

  const combinedEventWithRelationships = useMemo(() => {
    const allEventFields = [...eventFields, ...eventsConnectedToRelationships]

    return allEventFields.map(connectEventToRelationship)
  }, [eventFields, eventsConnectedToRelationships, connectEventToRelationship])

  return (
    <>
      {combinedEventWithRelationships.length > 0 && (
        <>
          <FieldWrapper indent="0">
            <H2 size="H5">{t('view-index.events', 'Events')}</H2>
          </FieldWrapper>
          <div css={indentCss}>
            {combinedEventWithRelationships.map((element, i) => (
              <IndexInfoSection
                element={element}
                onEditClick={() => {
                  openOverlay('eventPanel')
                  trackEvent({
                    event_name: 'click_action',
                    link_name: `Edit: Events: Open`,
                  })
                  dispatch({ type: 'SET_ACTIVE_ELEMENT_ID', value: element.id })
                }}
                disableEditButton={isRestrictedEventField || disableEdit}
                slsTypes={zionSlsState.types}
                isBottomSheet={infoSheetOverlay?.isBottomSheet}
                growX
                key={element.id}
                data-testid={`edit-event-${i}`}
                indented
              />
            ))}

            <Row>
              <FieldWrapper indent="0">
                <H3 size="H6">{t('view-index.event', 'New Event')}</H3>
              </FieldWrapper>
              <IconButton
                title={t('view-index.add-event', 'Add new event')}
                data-testid="add-event-button"
                disabled={isRestrictedEventField || disableEdit}
                onClick={newEventClick}
                Icon={ContentAdd}
              />
            </Row>
          </div>
        </>
      )}
      {combinedEventWithRelationships.length === 0 && (
        <>
          <IndexInfoSection
            element={{ label: t('view-index.events', 'Events'), elementType: 'events' }}
            onEditClick={newEventClick}
            disableEditButton={isRestrictedEventField || isRestrictedRelationshipField || disableEdit}
            slsTypes={zionSlsState.types}
            isBottomSheet={infoSheetOverlay?.isBottomSheet}
            growX
            data-testid="add-event"
          />
          <Subsection>
            <FieldWrapper>
              <NoDataAddedMessage />
            </FieldWrapper>
          </Subsection>
        </>
      )}
    </>
  )
}
export default EventsSection
